import React from "react";

import {
  BodyText,
  Collapsible,
  HeaderText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui";

import { useLabLocations } from "./hooks";

import type { DxReportViewTest } from "../../network/useDxReportView";
import { Finding } from "./Finding";

const LAB_RESULT_COLUMNS = [
  "Test",
  "Status",
  "Result",
  "Flag",
  "Range",
  "Facility",
];

export const Findings = ({ tests = [] }: { tests: DxReportViewTest[] }) => {
  const { getLabLocationIndex } = useLabLocations(tests);

  if (!tests.length) {
    return (
      <Collapsible title="Results" defaultOpen={true}>
        <BodyText textColor="tertiary" size="md">
          See PDF for full report.
        </BodyText>
      </Collapsible>
    );
  }

  const resultFindingContent = tests.map((test) => (
    <React.Fragment key={`${test.curaiServiceRequestId}`}>
      {test.testName && (
        <TableRow>
          <TableCell colSpan={LAB_RESULT_COLUMNS.length}>
            <HeaderText size="sm">{test.testName}</HeaderText>
          </TableCell>
        </TableRow>
      )}
      {test.testResults.map((testResult) => {
        if (!testResult) {
          return null;
        }

        const labLocation = testResult.performingLabFacility
          ? getLabLocationIndex(testResult.performingLabFacility) + 1
          : null;
        const hasComments = testResult.resultComments?.length;
        return (
          <React.Fragment key={`${testResult.curaiObservationId}-value`}>
            <Finding
              testResult={testResult}
              labLocation={labLocation}
              showSeparator={!hasComments}
            />
            {testResult.resultComments?.map((comment) => (
              <TableRow key={comment.comment}>
                <TableCell colSpan={LAB_RESULT_COLUMNS.length}>
                  <BodyText size="sm" className="font-mono whitespace-pre">
                    {comment.comment}
                  </BodyText>
                </TableCell>
              </TableRow>
            ))}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  ));

  return (
    <Collapsible title="Results" defaultOpen={true}>
      <Table condensed>
        <TableHeader>
          <TableRow>
            {LAB_RESULT_COLUMNS.map((title) => (
              <TableHead key={title} colSpan={1}>
                <HeaderText size="md">{title}</HeaderText>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>{resultFindingContent}</TableBody>
      </Table>
    </Collapsible>
  );
};
